import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Alert,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import Loader from "../Common/Loader";
import SnackbarComponent from "../Common/SnackbarComponent";
import { PDFDownloadLink } from "@react-pdf/renderer";
import InvoicePDF from "./InvoicePdf";
import { Print, EditOutlined } from "@mui/icons-material";
import Spinner from "../Common/Spinner";
import api from "../Auth/Middleware";

const toInputDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toISOString().split("T")[0];
};

const AddFeesDialog = ({
  open,
  onClose,
  studentDetail,
  id,
  batchEnrollmentId,
  batch,
  onSuccess,
  onFail,
}) => {
  const role = sessionStorage.getItem("role");
  const isAdmin = role === "Admin";

  const [loading, setLoading] = useState(true);
  const [feesData, setFeesData] = useState(null);
  const [totalFees, setTotalFees] = useState("");
  const [dateOfPayment, setDateOfPayment] = useState("");
  const [nextDue, setNextDue] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [pendingAmount, setpendingAmount] = useState("");
  const [snackbarMsg, setSnackbarMSg] = useState("");
  const [snackbarSevierty, setSnackbarSevierty] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Edit mode state
  const [editingFee, setEditingFee] = useState(null);
  const [editPaidAmount, setEditPaidAmount] = useState("");
  const [editSubmitDate, setEditSubmitDate] = useState("");
  const [editNextDue, setEditNextDue] = useState("");
  const [editTotalAmount, setEditTotalAmount] = useState("");

  const fetchFees = async () => {
    try {
      const response = await api.get(`Student/Fees/${id}`);
      setFeesData(response.data);
      setTotalFees(response?.data[0]?.total);
      setLoading(false);
    } catch (error) {
      setSnackbarOpen(true);
      setSnackbarMSg("Something went Wrong!! Please refresh the page");
      setSnackbarSevierty("error");
    }
  };

  useEffect(() => {
    fetchFees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (feesData) {
      const sum = feesData.reduce(
        (accumulator, currentValue) => accumulator + currentValue.amoutPaid,
        0
      );
      setpendingAmount(feesData[0]?.total - sum);
    }
  }, [feesData]);

  const handleEditClick = (fee) => {
    setEditingFee(fee);
    setEditPaidAmount(fee.amoutPaid);
    setEditSubmitDate(toInputDate(fee.date));
    setEditNextDue(toInputDate(fee.nextDue));
    setEditTotalAmount(fee.total);
  };

  const handleCancelEdit = () => {
    setEditingFee(null);
  };

  const handleSaveEdit = async () => {
    if (!editSubmitDate) {
      setSnackbarOpen(true);
      setSnackbarMSg("Please enter a valid date of payment");
      setSnackbarSevierty("warning");
      return;
    }
    if (editNextDue && editNextDue <= editSubmitDate) {
      setSnackbarOpen(true);
      setSnackbarMSg("Next due date can't be less than date of payment");
      setSnackbarSevierty("warning");
      return;
    }
    if (!editPaidAmount || isNaN(parseFloat(editPaidAmount)) || parseFloat(editPaidAmount) <= 0) {
      setSnackbarOpen(true);
      setSnackbarMSg("Please enter a valid paid amount");
      setSnackbarSevierty("warning");
      return;
    }
    if (!editTotalAmount || isNaN(parseFloat(editTotalAmount)) || parseFloat(editTotalAmount) <= 0) {
      setSnackbarOpen(true);
      setSnackbarMSg("Please enter a valid total amount");
      setSnackbarSevierty("warning");
      return;
    }

    const data = {
      amountPaid: parseFloat(editPaidAmount),
      submitDate: editSubmitDate,
      nextDue: editNextDue ? editNextDue : null,
      totalAmount: parseFloat(editTotalAmount),
    };

    try {
      await api.patch(`Student/EditFees/${editingFee.id}`, data);
      setEditingFee(null);
      setLoading(true);
      await fetchFees();
      setSnackbarOpen(true);
      setSnackbarMSg("Fees updated successfully");
      setSnackbarSevierty("success");
    } catch (error) {
      setSnackbarOpen(true);
      setSnackbarMSg("Failed to update fees. Please try again.");
      setSnackbarSevierty("error");
    }
  };

  const handleAddFees = async () => {
    if (
      !totalFees ||
      isNaN(parseFloat(totalFees)) ||
      parseFloat(totalFees) <= 0
    ) {
      setSnackbarOpen(true);
      setSnackbarMSg("Please enter a valid total amount");
      setSnackbarSevierty("warning");
      return;
    }

    if (!dateOfPayment) {
      setSnackbarOpen(true);
      setSnackbarMSg("Please enter a valid date of payment");
      setSnackbarSevierty("warning");
      return;
    }

    if (nextDue && nextDue <= dateOfPayment) {
      setSnackbarOpen(true);
      setSnackbarMSg("Next due date can't be less than date of payment ");
      setSnackbarSevierty("warning");
      return;
    }

    if (
      !paidAmount ||
      isNaN(parseFloat(paidAmount)) ||
      parseFloat(paidAmount) <= 0
    ) {
      setSnackbarOpen(true);
      setSnackbarMSg("Please enter a valid amount ");
      setSnackbarSevierty("warning");
      return;
    }

    if (paidAmount > pendingAmount) {
      setSnackbarOpen(true);
      setSnackbarMSg(
        "Please enter valid amount. Paid amount cannot be more than pending amount"
      );
      setSnackbarSevierty("warning");
      return;
    }

    const data = {
      studentId: id,
      amountPaid: parseFloat(paidAmount),
      submitDate: dateOfPayment,
      nextDue: nextDue ? nextDue : null,
      totalAmount: parseFloat(totalFees),
      batchEnrollmentId: batchEnrollmentId,
    };

    try {
      await api.post('Student/AddFees', data);
      onSuccess();
      onClose();
    } catch (error) {
      onFail();
      onClose();
    }
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        severity={snackbarSevierty}
        message={snackbarMsg}
        onClose={closeSnackbar}
      ></SnackbarComponent>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Add Fees for {studentDetail.name}</DialogTitle>
        <DialogContent>
          {batch && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Currently adding fees for batch <strong>{batch}</strong>
            </Alert>
          )}
          {loading ? (
            <Loader />
          ) : feesData && feesData.length > 0 ? (
            <div>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Paid Amount</TableCell>
                    <TableCell>Date of Payment</TableCell>
                    <TableCell>Next Due</TableCell>
                    <TableCell>Invoice</TableCell>
                    {isAdmin && <TableCell>Edit</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feesData.map((fee) => (
                    <TableRow
                      key={fee.id}
                      selected={editingFee?.id === fee.id}
                    >
                      <TableCell>{fee.amoutPaid}</TableCell>
                      <TableCell>
                        {new Date(fee.date).toLocaleDateString("en-GB")}
                      </TableCell>
                      <TableCell>
                        {new Date(fee.nextDue).toLocaleDateString("en-GB")}
                      </TableCell>
                      <TableCell>
                        <PDFDownloadLink
                          document={<InvoicePDF feesData={fee} studentData={studentDetail} allFees={feesData} />}
                          fileName={`INV_${studentDetail.name.replace(/ /g, "_")}.pdf`}
                        >
                          {({ loading }) =>
                            loading ? (
                              <Spinner />
                            ) : (
                              <IconButton color="info" aria-label="Print">
                                <Print />
                              </IconButton>
                            )
                          }
                        </PDFDownloadLink>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <IconButton
                            color="primary"
                            aria-label="Edit"
                            onClick={() => handleEditClick(fee)}
                          >
                            <EditOutlined />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="my-3">
                <Stack direction="row" spacing={2}>
                  <Chip
                    color="info"
                    label={`Total Fees: \u20B9${feesData[0]?.total}`}
                  />
                  <Chip
                    color="secondary"
                    label={`Pending Fees: \u20B9${pendingAmount}`}
                  />
                </Stack>
              </div>

              {editingFee ? (
                <div className="my-2">
                  <Box display="flex" flexDirection="column" gap={2}>
                    <h4>Edit installment</h4>
                    <TextField
                      label="Total Fees"
                      type="number"
                      value={editTotalAmount}
                      onChange={(e) => setEditTotalAmount(e.target.value)}
                    />
                    <TextField
                      label="Date of Payment"
                      type="date"
                      required
                      value={editSubmitDate}
                      onChange={(e) => setEditSubmitDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Next Due"
                      type="date"
                      value={editNextDue}
                      onChange={(e) => setEditNextDue(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Paid Amount"
                      type="number"
                      value={editPaidAmount}
                      onChange={(e) => setEditPaidAmount(e.target.value)}
                    />
                    <Stack direction="row" spacing={2}>
                      <Button variant="contained" color="primary" onClick={handleSaveEdit}>
                        Save
                      </Button>
                      <Button variant="outlined" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </Stack>
                  </Box>
                </div>
              ) : (
                <div className="my-2">
                  <Box display="flex" flexDirection="column" gap={2}>
                    <h4>Add new installment</h4>
                    <TextField
                      label="Date of Payment"
                      type="date"
                      required
                      value={dateOfPayment}
                      onChange={(e) => setDateOfPayment(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Next Due"
                      type="date"
                      value={nextDue}
                      onChange={(e) => setNextDue(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Paid Amount"
                      type="number"
                      value={paidAmount}
                      onChange={(e) => setPaidAmount(e.target.value)}
                    />
                  </Box>
                </div>
              )}
            </div>
          ) : (
            <div className="my-2">
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Total Fees"
                  required
                  value={totalFees}
                  type="number"
                  onChange={(e) => setTotalFees(e.target.value)}
                />
                <TextField
                  label="Date of Payment"
                  value={dateOfPayment}
                  type="date"
                  onChange={(e) => setDateOfPayment(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Next Due"
                  value={nextDue}
                  type="date"
                  onChange={(e) => setNextDue(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Paid Amount"
                  value={paidAmount}
                  type="number"
                  onChange={(e) => setPaidAmount(e.target.value)}
                />
              </Box>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          {!editingFee && (
            <Button onClick={handleAddFees} variant="contained" color="primary">
              Add Fees
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddFeesDialog;
